import {toRomanNumeral} from 'common/HelperFunctions'
import React, {FC, useMemo} from 'react'

type Props = {
  labels: string[]
  activeIndex: number
  maxReachedIndex: number
  completing?: boolean
  isDark?: boolean
  isStepClickable?: (idx: number) => boolean
  onStepClick?: (idx: number) => void
}

const HorizontalStepper: FC<Props> = ({
  labels,
  activeIndex,
  maxReachedIndex,
  completing = false,
  isDark = false,
  isStepClickable,
  onStepClick,
}) => {
  const canClick = useMemo(
    () => (idx: number) => {
      if (!onStepClick) return false
      if (typeof isStepClickable === 'function') return isStepClickable(idx)
      return idx <= maxReachedIndex
    },
    [isStepClickable, maxReachedIndex, onStepClick]
  )

  const progressPct =
    maxReachedIndex === 0 ? 0 : (maxReachedIndex / Math.max(1, labels.length - 1)) * 100

  const trackColor = isDark ? '#2b3a52' : '#e4e6ef'
  const inactiveIconBg = isDark ? '#2b2b40' : '#e4e6ef'
  const inactiveIconColor = isDark ? '#92929f' : '#7e8299'

  return (
    <div
      className='step2-horizontal-stepper'
      style={{'--step-count': labels.length} as React.CSSProperties}
    >
      <div
        className='step2-h-track'
        style={{
          background:
            maxReachedIndex === 0
              ? `linear-gradient(90deg, ${trackColor} 0%, ${trackColor} 100%)`
              : `linear-gradient(90deg, var(--primary-color, #056EE9) 0%, var(--primary-color, #056EE9) ${progressPct}%, ${trackColor} ${progressPct}%)`,
          height: 4,
          borderRadius: 2,
        }}
      />
      {labels.map((label, idx) => {
        const isActive = idx === activeIndex
        const isCompleted = idx < maxReachedIndex
        const isClickable = canClick(idx)
        return (
          <div
            key={idx}
            className={`step2-h-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isActive && completing ? 'completing' : ''} ${isClickable ? 'step2-h-step--clickable' : ''}`}
            style={{cursor: isClickable ? 'pointer' : 'default'}}
            onClick={() => {
              if (!isClickable) return
              if (idx === activeIndex) return
              onStepClick?.(idx)
            }}
            onKeyDown={(e) => {
              if (!isClickable) return
              if (idx === activeIndex) return
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onStepClick?.(idx)
              }
            }}
            role='button'
            tabIndex={isClickable ? 0 : -1}
          >
            <div
              className='step2-h-step-icon'
              style={{
                backgroundColor:
                  isActive || isCompleted ? 'var(--primary-color, #056EE9)' : inactiveIconBg,
                color: isActive || isCompleted ? '#ffffff' : inactiveIconColor,
                borderColor:
                  isActive || isCompleted ? 'var(--primary-color, #056EE9)' : inactiveIconBg,
                width: 40,
                height: 40,
                fontSize: '1.2rem',
                boxShadow: isActive ? '0 0 15px rgba(5, 110, 233, 0.4)' : 'none',
                transition: 'all 0.3s ease',
              }}
            >
              {idx < maxReachedIndex ? (
                <span className='step2-tick-icon'>✓</span>
              ) : idx === activeIndex && completing ? (
                <span className='step2-tick-icon'>✓</span>
              ) : (
                toRomanNumeral(idx + 1)
              )}
            </div>
            <div
              className='step2-h-step-label'
              style={{
                color: isActive
                  ? 'var(--primary-color, #056EE9)'
                  : isCompleted
                    ? isDark
                      ? '#92929f'
                      : '#7E8299'
                    : '#7e8299',
                fontWeight: isActive ? 700 : 500,
              }}
            >
              {label}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default HorizontalStepper
